import { Pencil, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { deleteReading, listReadings, updateReading } from '~/api/monitoring'
import type { AdminReading } from '~/api/types'
import { ConfirmDialog } from '~/components/ConfirmDialog'
import { Badge, type BadgeTone } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { SearchInput } from '~/components/ui/search-input'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '~/components/ui/table'
import { useToast } from '~/components/ui/toast'
import { formatDateTime } from '~/lib/utils'
import { useAuthStore } from '~/stores/auth-store'

// 측정값(readings) 관리 표. 식별자·지표·값(+단위)·소스·측정시각 + 행 단위 인라인 편집(값/단위)
// 과 삭제(superadmin). 제어 후 onChanged로 부모가 상세(차트·알림·집계)를 다시 평가한다.
const PAGE_SIZE = 20

const SOURCE_LABELS: Record<string, string> = {
    manual: '수기',
    excel: '엑셀',
    api: 'API',
    image: '이미지',
}

const SOURCE_TONES: Record<string, BadgeTone> = {
    manual: 'neutral',
    excel: 'info',
    api: 'success',
    image: 'warning',
}

export function ReadingsManager({
    dashboardId,
    onChanged,
}: {
    dashboardId: string
    /** 수정/삭제 성공 후 호출 — 부모가 상세를 refetch해 차트·알림을 갱신한다. */
    onChanged?: () => void | Promise<void>
}) {
    const toast = useToast()
    // 삭제는 superadmin에게만 노출(서버도 게이트). 역할이 다르면 버튼을 숨긴다.
    const isSuperadmin = useAuthStore((s) => s.adminRole) === 'superadmin'
    const [items, setItems] = useState<AdminReading[]>([])
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)
    const [q, setQ] = useState('')
    const [offset, setOffset] = useState(0)
    // 측정값 변경 후 목록만 다시 불러오는 트리거(q/offset과 무관한 재조회).
    const [reloadKey, setReloadKey] = useState(0)

    // 행 단위 인라인 편집 상태(편집 중 rid + 값/단위 드래프트).
    const [editId, setEditId] = useState<string | null>(null)
    const [draft, setDraft] = useState({ value: '', unit: '' })
    const [saving, setSaving] = useState(false)
    const [confirmDelete, setConfirmDelete] = useState<AdminReading | null>(
        null
    )

    useEffect(() => {
        let alive = true
        setLoading(true)
        setError(null)
        listReadings(dashboardId, {
            q: q.trim() || undefined,
            limit: PAGE_SIZE,
            offset,
        })
            .then((data) => {
                if (alive) setItems(data)
            })
            .catch((e) => {
                if (alive) setError((e as Error).message)
            })
            .finally(() => {
                if (alive) setLoading(false)
            })
        return () => {
            alive = false
        }
    }, [dashboardId, q, offset, reloadKey])

    // 수정/삭제 후 측정값 목록 + 부모 상세(차트·알림·집계)를 함께 다시 불러온다.
    async function refresh() {
        setReloadKey((k) => k + 1)
        await onChanged?.()
    }

    function startEdit(r: AdminReading) {
        setEditId(r.id)
        setDraft({
            value: r.value != null ? String(r.value) : '',
            unit: r.unit ?? '',
        })
    }

    async function saveEdit(r: AdminReading) {
        const num = Number(draft.value.trim())
        if (draft.value.trim() === '' || Number.isNaN(num)) {
            toast.error('값은 숫자로 입력해 주세요.')
            return
        }
        setSaving(true)
        try {
            await updateReading(dashboardId, r.id, {
                value: num,
                unit: draft.unit.trim(),
            })
            toast.success('측정값을 수정했어요.')
            setEditId(null)
            await refresh()
        } catch (e) {
            toast.error((e as Error).message)
        } finally {
            setSaving(false)
        }
    }

    async function remove() {
        if (!confirmDelete) return
        try {
            await deleteReading(dashboardId, confirmDelete.id)
            toast.success('측정값을 삭제했어요. 알림이 재평가됩니다.')
            setConfirmDelete(null)
            await refresh()
        } catch (e) {
            toast.error((e as Error).message)
        }
    }

    return (
        <div>
            <div className='mb-4'>
                <SearchInput
                    className='w-72'
                    value={q}
                    onChange={(v) => {
                        setOffset(0)
                        setQ(v)
                    }}
                    placeholder='값·지표 검색'
                />
            </div>

            {error ? (
                <p className='text-destructive mb-4 text-[13px]'>{error}</p>
            ) : null}

            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>식별자</TableHead>
                        <TableHead>지표</TableHead>
                        <TableHead>값</TableHead>
                        <TableHead>소스</TableHead>
                        <TableHead>측정시각</TableHead>
                        <TableHead className='text-right'>조치</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {loading ? (
                        <TableRow>
                            <TableCell
                                colSpan={6}
                                className='text-muted-foreground py-8 text-center'>
                                불러오는 중…
                            </TableCell>
                        </TableRow>
                    ) : items.length === 0 ? (
                        <TableRow>
                            <TableCell
                                colSpan={6}
                                className='text-muted-foreground py-8 text-center'>
                                측정값이 없어요.
                            </TableCell>
                        </TableRow>
                    ) : (
                        items.map((r) => {
                            const editing = editId === r.id
                            return (
                                <TableRow key={r.id}>
                                    <TableCell className='font-mono text-xs'>
                                        {r.id.slice(0, 8)}
                                    </TableCell>
                                    <TableCell className='font-medium'>
                                        {r.nameKo || r.fieldId || '—'}
                                    </TableCell>
                                    <TableCell>
                                        {editing ? (
                                            <div className='flex items-center gap-1.5'>
                                                <Input
                                                    size='sm'
                                                    type='number'
                                                    className='w-24'
                                                    value={draft.value}
                                                    aria-label='값'
                                                    onChange={(e) =>
                                                        setDraft((d) => ({
                                                            ...d,
                                                            value: e.target
                                                                .value,
                                                        }))
                                                    }
                                                />
                                                <Input
                                                    size='sm'
                                                    className='w-20'
                                                    placeholder='단위'
                                                    value={draft.unit}
                                                    aria-label='단위'
                                                    onChange={(e) =>
                                                        setDraft((d) => ({
                                                            ...d,
                                                            unit: e.target
                                                                .value,
                                                        }))
                                                    }
                                                />
                                            </div>
                                        ) : (
                                            <>
                                                <span className='tnum'>
                                                    {r.value ?? '—'}
                                                </span>
                                                {r.unit ? (
                                                    <span className='text-muted-foreground'>
                                                        {' '}
                                                        {r.unit}
                                                    </span>
                                                ) : null}
                                            </>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {r.source ? (
                                            <Badge
                                                tone={
                                                    SOURCE_TONES[r.source] ??
                                                    'neutral'
                                                }>
                                                {SOURCE_LABELS[r.source] ??
                                                    r.source}
                                            </Badge>
                                        ) : (
                                            '—'
                                        )}
                                    </TableCell>
                                    <TableCell className='whitespace-nowrap'>
                                        {formatDateTime(r.measuredAt)}
                                    </TableCell>
                                    <TableCell className='text-right'>
                                        <div className='flex items-center justify-end gap-1.5'>
                                            {editing ? (
                                                <>
                                                    <Button
                                                        variant='outline'
                                                        size='xs'
                                                        disabled={saving}
                                                        onClick={() =>
                                                            setEditId(null)
                                                        }>
                                                        취소
                                                    </Button>
                                                    <Button
                                                        size='xs'
                                                        disabled={saving}
                                                        onClick={() =>
                                                            saveEdit(r)
                                                        }>
                                                        저장
                                                    </Button>
                                                </>
                                            ) : (
                                                <>
                                                    <Button
                                                        variant='outline'
                                                        size='xs'
                                                        onClick={() =>
                                                            startEdit(r)
                                                        }>
                                                        <Pencil />
                                                        편집
                                                    </Button>
                                                    {isSuperadmin ? (
                                                        <Button
                                                            variant='outline'
                                                            size='xs'
                                                            onClick={() =>
                                                                setConfirmDelete(
                                                                    r
                                                                )
                                                            }>
                                                            <Trash2 />
                                                            삭제
                                                        </Button>
                                                    ) : null}
                                                </>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            )
                        })
                    )}
                </TableBody>
            </Table>

            <div className='mt-4 flex items-center justify-between'>
                <span className='text-muted-foreground text-xs'>
                    {items.length > 0
                        ? `${offset + 1}–${offset + items.length}`
                        : '0'}
                </span>
                <div className='flex gap-2'>
                    <Button
                        variant='outline'
                        size='sm'
                        disabled={offset === 0 || loading}
                        onClick={() =>
                            setOffset((o) => Math.max(0, o - PAGE_SIZE))
                        }>
                        이전
                    </Button>
                    <Button
                        variant='outline'
                        size='sm'
                        disabled={items.length < PAGE_SIZE || loading}
                        onClick={() => setOffset((o) => o + PAGE_SIZE)}>
                        다음
                    </Button>
                </div>
            </div>

            <ConfirmDialog
                open={confirmDelete !== null}
                title='측정값을 삭제할까요?'
                description='측정값을 삭제하면 알림이 재평가됩니다. 이 작업은 되돌릴 수 없어요.'
                confirmLabel='삭제'
                danger
                onConfirm={remove}
                onCancel={() => setConfirmDelete(null)}
            />
        </div>
    )
}
